
import React from 'react';
import { motion } from 'framer-motion';
import { 
  Layers, Zap, Bot, Database, Server, Smartphone, 
  Layout, Search, Cpu, Globe, Shield, Code2, 
  Orbit, Activity, Terminal, Fingerprint
} from 'lucide-react';

const Services: React.FC = () => {
  const serviceCategories = [
    {
      title: "Custom Software Development",
      icon: <Code2 className="w-6 h-6" />,
      tag: "SOFTWARE",
      desc: "End-to-end software solutions tailored to your business needs. From concept to deployment, we build scalable applications that drive results.",
      metrics: { delivery: "On-Time", quality: "Premium", support: "24/7" },
      items: ["Enterprise Software Solutions", "SaaS Platform Development", "API-First Architecture", "Cloud-Native Applications", "Legacy System Modernization"],
      color: "text-blue-400"
    },
    {
      title: "Mobile Applications",
      icon: <Smartphone className="w-6 h-6" />,
      tag: "MOBILE",
      desc: "High-performance mobile applications designed for the modern user. We prioritize 'Metal-Close' responsiveness and fluid UX across iOS and Android.",
      metrics: { fps: "120hz", size: "Optimized", offline: "First-Class" },
      items: ["React Native & Flutter", "Native iOS & Android", "Biometric Integration", "Cross-Platform Development", "Mobile-First Design"],
      color: "text-accent"
    },
    {
      title: "Frontend Development",
      icon: <Layout className="w-6 h-6" />,
      tag: "FRONTEND",
      desc: "Beautiful, interactive interfaces that users love. We craft pixel-perfect experiences with modern frameworks and cutting-edge design systems.",
      metrics: { speed: "Blazing", responsive: "100%", a11y: "WCAG AAA" },
      items: ["React & Next.js Applications", "Vue & Angular Solutions", "Progressive Web Apps (PWA)", "Design System Implementation", "Performance Optimization"],
      color: "text-violet-400"
    },
    {
      title: "Backend Systems",
      icon: <Server className="w-6 h-6" />,
      tag: "BACKEND",
      desc: "We build the invisible engines that power global platforms. High-concurrency architecture meets rock-solid stability and scalable infrastructure.",
      metrics: { latency: "<50ms", uptime: "99.99%", throughput: "100k+ req/s" },
      items: ["RESTful & GraphQL APIs", "Microservices Architecture", "Real-time WebSocket Systems", "Serverless Computing", "API Gateway & Load Balancing"],
      color: "text-primary"
    },
    {
      title: "Core Backend Systems",
      icon: <Server className="w-6 h-6" />,
      tag: "INFRASTRUCTURE",
      desc: "We build the invisible engines that power global platforms. High-concurrency architecture meets rock-solid stability.",
      metrics: { latency: "<50ms", uptime: "99.99%", throughput: "100k+ req/s" },
      items: ["Distributed Microservices", "Python & Go Orchestration", "Real-time WebSocket Clusters", "Custom Search Engines", "Hardened Security Protocols"],
      color: "text-primary"
    },
    {
      title: "Mobile Ecosystems",
      icon: <Smartphone className="w-6 h-6" />,
      tag: "CLIENT-SIDE",
      desc: "High-performance mobile applications designed for the modern user. We prioritize 'Metal-Close' responsiveness and fluid UX.",
      metrics: { fps: "120hz", size: "Optimized", offline: "First-Class" },
      items: ["React Native & Flutter", "Native Module Bridging", "Biometric Integration", "Dynamic Asset Syncing", "Low-Latency Data Caching"],
      color: "text-accent"
    },
    {
      title: "Intelligent Automation",
      icon: <Bot className="w-6 h-6" />,
      tag: "AUTOMATION",
      desc: "Autonomous agents that handle the heavy lifting. We integrate LLMs and custom logic to automate complex business workflows.",
      metrics: { accuracy: "High", savings: "80%+", scale: "Infinite" },
      items: ["Custom LLM Workflows", "Browser Automation Engines", "Trading & Financial Bots", "System Health Monitors", "Smart Data Ingestion"],
      color: "text-emerald-400"
    },
    {
      title: "Database Architecture",
      icon: <Database className="w-6 h-6" />,
      tag: "STORAGE",
      desc: "Designing the memory of your application. We optimize for read/write heavy workloads and complex relational logic.",
      metrics: { indexing: "Optimized", redundancy: "Triple", query: "Sub-ms" },
      items: ["PostgreSQL Optimization", "Redis Caching Layers", "Elasticsearch Clusters", "Data Encryption at Rest", "Automated Migrations"],
      color: "text-indigo-400"
    }
  ];

  return (
    <div className="pt-40 pb-32 container mx-auto px-6 overflow-hidden">
      {/* Hero Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mb-32"
      >
        <div className="inline-flex items-center space-x-2 bg-zinc-900 border border-white/10 px-4 py-1.5 rounded-full mb-8">
          <span className="flex h-2 w-2 rounded-full bg-accent animate-pulse" />
          <span className="text-xs font-bold tracking-widest text-zinc-500 uppercase">Capabilities Matrix // v2.0</span>
        </div>
        <h1 className="text-6xl md:text-8xl font-display font-bold mb-10 tracking-tighter leading-none">
          Engineered for <br />
          <span className="text-accent italic font-light">Unmatched</span> Performance.
        </h1>
        <p className="text-xl md:text-2xl text-zinc-400 max-w-2xl leading-relaxed">
          We specialize in the heavy-duty engineering that makes digital giants thrive. From the deep backend to the mobile edge, we build systems that don't just work—they dominate.
        </p>
      </motion.div>

      {/* Main Services Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-40">
        {serviceCategories.map((category, i) => (
          <ServiceBlock key={i} category={category} index={i} />
        ))}
      </div>

      {/* Methodology: The Binary Lifecycle */}
      <section className="relative">
        <div className="text-center mb-32">
          <h2 className="text-4xl md:text-6xl font-display font-bold mb-6 tracking-tight">The Binary <span className="text-accent italic font-light">Lifecycle</span></h2>
          <p className="text-zinc-500 max-w-xl mx-auto uppercase text-[10px] tracking-[0.5em] font-bold">From Logical Design to Global Deployment</p>
        </div>
        
        <div className="relative max-w-6xl mx-auto px-4">
          <div className="absolute top-1/2 left-0 right-0 h-[1px] bg-zinc-800/50 hidden lg:block -translate-y-1/2" />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 relative">
            <LifecycleStep 
              step="01" 
              title="Logic Blueprint" 
              desc="Lionel architects the system logic, defining data flows and reasoning paths."
              icon={<Search className="w-5 h-5"/>} 
            />
            <LifecycleStep 
              step="02" 
              title="Core Execution" 
              desc="Maasi builds the physical infrastructure and core backend service layers."
              icon={<Cpu className="w-5 h-5"/>} 
            />
            <LifecycleStep 
              step="03" 
              title="Native Synthesis" 
              desc="We fuse the backend with high-performance mobile and web interfaces."
              icon={<Fingerprint className="w-5 h-5"/>} 
            />
            <LifecycleStep 
              step="04" 
              title="Global Uplink" 
              desc="Deployment to distributed edge networks with 24/7 technical monitoring."
              icon={<Globe className="w-5 h-5"/>} 
            />
          </div>
        </div>
      </section>

      {/* Summary CTA */}
      <motion.div 
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mt-64 text-center bg-zinc-900/40 border border-white/5 p-20 rounded-[48px] relative overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-accent to-transparent opacity-30" />
        <h2 className="text-4xl md:text-6xl font-display font-bold mb-8">Ready to Build?</h2>
        <p className="text-zinc-400 text-lg mb-12 max-w-xl mx-auto">
          We are currently taking reservations for new high-scale projects. Secure your engineering slot for the next quarter.
        </p>
        <motion.button 
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-white text-background px-12 py-5 rounded-full font-bold text-lg hover:bg-accent hover:text-white transition-all interactive shadow-2xl"
        >
          Request Technical Consult
        </motion.button>
      </motion.div>
    </div>
  );
};

// Fix: Using 'any' for props to ensure the 'key' property is accepted when this component is used within a .map() loop.
const ServiceBlock = ({ category, index }: any) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="group bg-zinc-900/30 border border-white/5 p-10 md:p-14 rounded-[48px] hover:bg-zinc-900/60 hover:border-accent/20 transition-all duration-700 relative overflow-hidden backdrop-blur-sm"
    >
      <div className="flex justify-between items-start mb-12">
        <div className={`p-6 bg-zinc-950 border border-white/5 rounded-3xl ${category.color} group-hover:scale-110 group-hover:rotate-6 transition-transform duration-500 shadow-2xl`}>
          {category.icon}
        </div>
        <div className="text-right">
          <div className="text-[10px] font-mono font-bold text-zinc-600 tracking-[0.3em] uppercase mb-1">{category.tag}</div>
          <div className="flex space-x-3 justify-end">
            {Object.entries(category.metrics).map(([key, val]: any) => (
              <div key={key} className="text-[9px] font-mono bg-zinc-950/50 border border-white/5 px-2 py-1 rounded text-zinc-400">
                <span className="text-accent/50 mr-1 opacity-50 uppercase">{key}:</span> {val}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <h3 className="text-3xl md:text-4xl font-display font-bold mb-6 group-hover:text-white transition-colors tracking-tight">{category.title}</h3>
      <p className="text-zinc-500 text-lg mb-10 leading-relaxed">{category.desc}</p>
      
      <div className="space-y-4 relative z-10">
        {category.items.map((item: string, j: number) => (
          <div key={j} className="flex items-center space-x-4 group/item">
            <div className={`w-1 h-1 rounded-full bg-zinc-800 group-hover/item:bg-accent group-hover/item:scale-150 transition-all`} />
            <span className="text-zinc-400 text-sm font-medium group-hover/item:text-zinc-200 transition-colors">{item}</span>
          </div>
        ))}
      </div>
      
      {/* Decorative Technical Background */}
      <div className="absolute bottom-[-50px] right-[-50px] w-64 h-64 bg-accent/5 blur-[100px] rounded-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
      
      {/* Ghost Code Snippet Overlay (Subtle) */}
      <div className="absolute top-10 right-10 font-mono text-[8px] text-accent/5 pointer-events-none group-hover:text-accent/10 transition-colors">
        <pre>{`async def handle_stream(node_id):\n  await uplink.connect(node_id)\n  while True:\n    data = await node.read()\n    process(data)`}</pre>
      </div>
    </motion.div>
  );
}

const LifecycleStep = ({ step, title, desc, icon }: { step: string, title: string, desc: string, icon: React.ReactNode }) => {
  return (
    <motion.div 
      whileHover={{ y: -10 }}
      className="relative z-10 flex flex-col items-center text-center p-8 bg-background border border-white/5 lg:border-transparent rounded-[32px] hover:bg-zinc-900/50 hover:border-white/5 transition-all group"
    >
      <div className="w-16 h-16 bg-zinc-900 border border-white/10 rounded-2xl flex items-center justify-center text-accent mb-8 group-hover:scale-110 group-hover:bg-accent group-hover:text-background transition-all duration-500 shadow-xl relative">
        {icon}
        {/* Step Indicator Dot */}
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-zinc-950 border border-white/10 rounded-full flex items-center justify-center text-[8px] font-bold text-zinc-500">
          {step}
        </div>
      </div>
      <h4 className="text-xl font-display font-bold mb-4 tracking-tight">{title}</h4>
      <p className="text-zinc-500 text-sm leading-relaxed">{desc}</p>
      
      {/* Active Scanning Line */}
      <motion.div 
        animate={{ opacity: [0, 1, 0], y: [-20, 20] }}
        transition={{ duration: 4, repeat: Infinity }}
        className="absolute inset-0 bg-gradient-to-b from-transparent via-accent/5 to-transparent pointer-events-none rounded-[32px] hidden group-hover:block"
      />
    </motion.div>
  );
}

export default Services;
